import React from "react";
import { useParams, Link } from "react-router-dom";
import BrandSettingsEditor from "@/components/BrandSettingsEditor";
import BrandLogoLibrary from "@/components/BrandLogoLibrary";
import ClientSendGridSection from "@/components/ClientSendGridSection";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardCheck, ChevronRight } from "lucide-react";

/**
 * Per-client Settings tab.
 * Currently houses the report colour palette + brand font selector. Future
 * client-scoped settings (default report period, hidden platforms, etc.) can
 * be added as new sections below.
 */
export default function ClientSettingsPage() {
  const { clientId } = useParams();
  if (!clientId) return null;
  return (
    <div className="space-y-6" data-testid="client-settings-page">
      <div>
        <div className="label-overline">Workspace</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Client settings</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Branding, defaults, and other per-client preferences. These settings drive the
          appearance of the monthly PDF report and the branded client portal.
        </p>
      </div>

      {/* Onboarding checklist shortcut */}
      <Link
        to={`/clients/${clientId}/checklist`}
        className="block"
        data-testid="settings-checklist-link"
      >
        <Card className="rounded-sm border-zinc-200 hover:border-[#002FA7] transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#002FA7] text-white">
              <ClipboardCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-sm">Onboarding checklist</div>
              <div className="text-xs text-zinc-500">
                Track every access, platform connection and brand asset required to onboard this client.
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-400" />
          </CardContent>
        </Card>
      </Link>

      <BrandLogoLibrary clientId={clientId} />

      <BrandSettingsEditor clientId={clientId} />

      <ClientSendGridSection clientId={clientId} />
    </div>
  );
}
